import React from 'react';
import { createRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { gridStateActions } from 'app/components/Common/Grid/GridStateManagement/GridState.redux';
import OrderCandidateMatchGrid from './OrderCandidateMatchGrid';
import { useInjectReducer, useInjectSaga } from 'redux-injectors';
import {
  defaultChips,
  getOrderCandidateMatchActions,
  orderCandidateMatchReducer,
  orderCandidateMatchSliceKey,
} from './store/orderCandidateMatch.redux';
import { orderCandidateMatchSaga } from './store/orderCandidateMatch.saga';
import OrderCandidateMatchFilters from './OrderCandidateMatchFilters';
import OrderCandidateMatchChips from './OrderCandidateMatchChips';
import { selectOrderDetailsUIView } from '../../Store/Order.selectors';
import { selectPayload } from './store/orderCandidateMatch.selectors';
import { useTranslation } from 'react-i18next';
import { getFilterRequest, revalidateChipSelection } from 'app/components/GlobalSearch/helper';
import SearchGridWrapper from 'app/layout/pages/SearchGridWrapper';
import _ from 'lodash';
import { GetMomentOfDate } from 'utils/dates/moment';
import { selectGridState } from '@AMIEWEB/Common/Grid/GridStateManagement/GridState.selectors';
import { globalSearchActions } from 'store/redux-store/global-search/slice';
import { globalActions } from 'app/ApplicationRoot/Global.redux';
import { gridSelectionActions } from '@AMIEWEB/Common/Grid/GridSelection/GridSelection.redux';
import { RootState } from 'types';
import { candidateSearchFilterName } from '@AMIEWEB/GlobalSearch/Candidate/SearchUtils';
import {
  ICandidateSkillSet,
  getSelectedSkillSetFilter,
  getSkillSetFilter,
} from 'app/ComponentLibrary/Filter/CandidateSkillset';
import { selectGlobalBanner } from 'app/ApplicationRoot/Global.selectors';
import { IBanner } from 'app/models/Global/Global';
import { getStateNames } from './OrderCandidateMatchFilterUtils';

export const OrderCandidateMatchWrapper = props => {
  const formMethods = useForm();
  const { handleSubmit } = formMethods;
  const hiddenSubmitButton = createRef<HTMLButtonElement>();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const defaultColumn = { column: 'candidateId', direction: 'desc' };
  const { filterAttributes } = useSelector(selectGridState);
  const gridTag = 'OrderCandidateMatch';

  useInjectReducer({ key: orderCandidateMatchSliceKey, reducer: orderCandidateMatchReducer });
  useInjectSaga({ key: orderCandidateMatchSliceKey, saga: orderCandidateMatchSaga });
  const selectOrderUiDetails = useSelector(selectOrderDetailsUIView);
  const partialPayload = useSelector(selectPayload);
  const gridState = useSelector(selectGridState);
  const globalBanner: IBanner = useSelector(selectGlobalBanner);
  const gridPreferences = useSelector((state: RootState) => {
    return state.userPreferenceData.userGridPreferences.find(item => item.id === gridTag);
  });

  //EVENTS
  const onApplyFilter = async (values, newFilterChips?) => {
    const filters = getFilterRequest(values, newFilterChips);
    let availableStartDate: Date;
    if (values.availableStartDate?.startDate && selectOrderUiDetails.orderDetails.startDate) {
      //@ts-ignore
      availableStartDate = new Date(
        Math.min(
          values.availableStartDate?.startDate?.getTime(),
          new Date(selectOrderUiDetails.orderDetails.startDate).getTime(),
        ),
      );
    } else availableStartDate = values.availableStartDate?.startDate;
    const selectedChipFilters =
      newFilterChips || revalidateChipSelection(values, filterAttributes?.selectedChipFilters || {});

    dispatch(
      gridStateActions.setFilterAttributes({
        filterObject: Object.entries(values),
        selectedChipFilters: selectedChipFilters,
      }),
    );
    dispatch(gridStateActions.setPageAttributes({ pageSize: 250, pageNumber: 1 }));
    if (_.isEqual(values, defaultChips)) {
      dispatch(getOrderCandidateMatchActions.setSelectedFilters(null));
    } else {
      dispatch(
        getOrderCandidateMatchActions.setSelectedFilters({
          ...filters,
          states: getStateNames(values.states, filters.states), //gets the name of the states since API expects state names when searchType="candidate" bg:419257
        }),
      );
    }

    const candidateFilter = values[candidateSearchFilterName.candidateAgggreggate];
    const selectedCandidateChipId =
      selectedChipFilters?.[candidateSearchFilterName.candidateAgggreggate]?.[0]?.value?.value;
    const candidateSkillSetFilter = values[candidateSearchFilterName.candidateSkillSet];

    const selectedSKillSetFilterChips = selectedChipFilters?.[candidateSearchFilterName.candidateSkillSet];
    let skillsets: ICandidateSkillSet[] = [];
    if (selectedSKillSetFilterChips && selectedSKillSetFilterChips?.length > 0) {
      skillsets = getSelectedSkillSetFilter(
        candidateSkillSetFilter?.skillsets,
        selectedSKillSetFilterChips,
        candidateSearchFilterName.candidateSkillSet,
      );
    } else {
      skillsets = getSkillSetFilter(candidateSkillSetFilter?.skillsets, candidateSearchFilterName.candidateSkillSet);
    }

    const requestBody = {
      searchType: 'candidate',
      pageNumber: 1,
      pageSize: 250,
      keyword: '',
      translation: t,
      sortedColumn: gridState.sortedColumn || defaultColumn,
      multiSortedColumns: gridPreferences?.value?.multiSortedColumns || [defaultColumn],
      ...filters,
      states: getStateNames(values.states, filters.states), //gets the name of the states since API expects state names when searchType="candidate" bg:419257
      skillsetFilter: {
        allowableMismatch: candidateSkillSetFilter?.allowableMismatch || false,
        isVerifiedOnly: candidateSkillSetFilter?.isVerifiedOnly || false,
        operation: candidateSkillSetFilter?.operation || 'Or',
        isExactMatch: candidateSkillSetFilter?.isExactMatch || false,
        skillsets: skillsets,
      },
      candidateIds: selectedCandidateChipId
        ? [selectedCandidateChipId]
        : candidateFilter.selections.map(x => x.candidateId),
      orderStartDate: availableStartDate && GetMomentOfDate(availableStartDate).format('YYYY-MM-DD'),
      skillset: partialPayload.skillSets,
      divisionId: partialPayload.divisionId,
    };
    dispatch(getOrderCandidateMatchActions.getOrderCandidateMatchAction(requestBody));
  };

  React.useEffect(() => {
    return () => {
      dispatch(gridStateActions.reset());
      dispatch(globalSearchActions.reset());
      dispatch(globalActions.closeBanner());
      dispatch(
        gridSelectionActions.setGridSelections({
          selectedData: [],
        }),
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <FormProvider {...formMethods}>
        <form
          onSubmit={handleSubmit(filter => onApplyFilter(filter))}
          style={{ marginBottom: globalBanner.open ? '25px' : '' }}
        >
          <SearchGridWrapper
            filters={<OrderCandidateMatchFilters onApplyFilter={onApplyFilter} brands={props.brands} />}
            table={<OrderCandidateMatchGrid />}
            chips={<OrderCandidateMatchChips onApplyFilter={onApplyFilter} />}
            tableMinHeight={430}
            loading={false}
          />
          <button ref={hiddenSubmitButton} style={{ display: 'none' }} type="submit" />
        </form>
      </FormProvider>
    </>
  );
};
